import React from "react";
import { Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { PageLayout } from "@APP/components";

const useStyles = makeStyles((theme) => ({
  underline: {
    textDecoration: "underline",
  },
  list: {
    paddingLeft: 30,
  },
  subList: {
    paddingLeft: 40,
  },
}));

const PrivacyNoticeScreen = () => {
  const styles = useStyles();

  return (
    <PageLayout>
      <Box maxWidth={980} alignSelf="center" id="privacyPolicyBox">
        <Typography variant="h5" align="center" className={styles.underline}>
          Privacy Notice
        </Typography>
        <br />
        <Typography variant="body2">
          This Privacy Notice tells you how your personal data is used and protected when you access
          the InvoiceIt Mobile App and Web Services (together "InvoiceIt Services") and tells you
          about your privacy rights and how the law protects you.
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Who We Are</b>
        </Typography>
        <br />
        <Typography variant="body2">
          In this privacy notice when we say <b>"BankiFi"</b> or <b>"we"</b> or <b>"us"</b> or{" "}
          <b>"our"</b> we mean BankiFi Technology Limited.
        </Typography>
        <br />
        <Typography variant="body2">
          Company number: 10696778
          <br />
          Address: Centurion House, 129 Deansgate, Manchester, M3 3WR
          <br />
          Email:{" "}
          <u>
            <a href="mailto:invoiceit.support@bankifi.com">invoiceit.support@bankifi.com</a>
          </u>
        </Typography>
        <br />
        <Typography variant="body2">
          BankiFi is the data controller responsible for your personal data.
        </Typography>
        <br />
        <Typography variant="body2">
          When we say <b>"you"</b> or <b>"your"</b>, we mean the person whose personal data we
          process as a result of that person or another person's use of the InvoiceIt Services.
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Contents</b>
        </Typography>
        <br />
        <Typography variant="body2">
          <ul className={styles.list}>
            <li>Key Information and Who We Are</li>
            <li>The Personal Data We Collect</li>
            <li>How We Collect Your Personal Data</li>
            <li>How We Use Your Personal Data</li>
            <li>Purposes for Which We Use Your Personal Data</li>
            <li>How We Share Your Personal Data</li>
            <li>International Transfers of Your Personal Data</li>
            <li>Data Security</li>
            <li>Cookies and app analytics</li>
            <li>Keeping Your Personal Data</li>
            <li>Your Legal Rights</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Key Information</b>
        </Typography>
        <br />
        <Typography variant="body2">
          <ul className={styles.list}>
            <li>We keep our privacy notice under regular review</li>
            <li>This version was last updated on XX.06.2026</li>
            <li>You can obtain older versions by contacting us</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Contacting Us</b>
        </Typography>
        <br />
        <Typography variant="body2">
          Email:{" "}
          <u>
            <a href="mailto:invoiceit.support@bankifi.com">invoiceit.support@bankifi.com</a>
          </u>
          <br />
          Address: BankiFi Technology Limited, Beehive Lofts, Jersey Street, Manchester, M4 6JG
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Regulator</b>
        </Typography>
        <br />
        <Typography variant="body2">
          You have the right to make a complaint to the Information Commissioner's Office (ICO):{" "}
          <u>
            <a href="https://www.ico.org.uk">www.ico.org.uk</a>
          </u>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Links to Other Services</b>
        </Typography>
        <br />
        <Typography variant="body2">
          The InvoiceIt Services may include links to third-party websites, plug-ins and
          applications.
        </Typography>
        <br />
        <Typography variant="body2">
          We do not control these third-party services and are not responsible for their privacy
          notices.
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>The Personal Data We Collect</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We may collect, use, store and transfer the following types of personal data:
        </Typography>
        <br />
        <Typography variant="body2">
          <ul className={styles.list}>
            <li>
              <b>Identity Data</b>
              <ul className={styles.subList}>
                <li>First name</li>
                <li>Last name</li>
                <li>Username</li>
                <li>Title</li>
                <li>Bank account number</li>
                <li>Bank account holder name</li>
              </ul>
            </li>
            <li>
              <b>Contact Data</b>
              <ul className={styles.subList}>
                <li>Business name</li>
                <li>Billing address</li>
                <li>Email address</li>
                <li>Telephone numbers</li>
              </ul>
            </li>
            <li>
              <b>Transaction Data</b>
              <ul className={styles.subList}>
                <li>Payment details</li>
                <li>Transaction activity</li>
              </ul>
            </li>
            <li>
              <b>Technical Data</b>
              <ul className={styles.subList}>
                <li>IP address</li>
                <li>Login data</li>
                <li>Browser type and version</li>
                <li>Device and system information</li>
              </ul>
            </li>
            <li>
              <b>Profile Data</b>
              <ul className={styles.subList}>
                <li>Username and password</li>
                <li>Preferences</li>
                <li>Feedback and survey responses</li>
              </ul>
            </li>
            <li>
              <b>Usage Data</b>
              <ul className={styles.subList}>
                <li>How you use Invoice It Services</li>
              </ul>
            </li>
            <li>
              <b>Marketing and Communications Data</b>
              <ul className={styles.subList}>
                <li>Marketing preferences</li>
                <li>Communication preferences</li>
              </ul>
            </li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Aggregated Data</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We may also use aggregated data for analysis and service improvement.
        </Typography>
        <br />
        <Typography variant="body2">
          We do not collect:
          <ul className={styles.list}>
            <li>Special categories of personal data</li>
            <li>Criminal conviction data</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>How We Collect Your Personal Data</b>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Direct Interactions</b>
        </Typography>
        <br />
        <Typography variant="body2">
          You provide data when you:
          <ul className={styles.list}>
            <li>Create an account</li>
            <li>Use the services</li>
            <li>Contact us</li>
            <li>Request marketing</li>
            <li>Enter surveys or promotions</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Automated Technologies</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We collect technical data via:
          <ul className={styles.list}>
            <li>Server logs</li>
            <li>Similar technologies</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Third Parties / Public Sources</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We receive data from:
          <ul className={styles.list}>
            <li>Analytics providers</li>
            <li>Public sources such as Companies House</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>How We Use Your Personal Data</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We only use your personal data when legally permitted, including:
          <ul className={styles.list}>
            <li>To perform a contract</li>
            <li>For legitimate interests</li>
            <li>To comply with legal obligations</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Legitimate Interests Include</b>
          <ul className={styles.list}>
            <li>Running our business</li>
            <li>Improving services</li>
            <li>Preventing fraud</li>
            <li>Maintaining records</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Purposes for Which We Use Your Personal Data</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We use your data to:
          <ul className={styles.list}>
            <li>Register you as a customer</li>
            <li>Provide InvoiceIt Services</li>
            <li>Manage our relationship with you</li>
            <li>Improve services and user experience</li>
            <li>Administer and protect our business</li>
            <li>Deliver relevant content and marketing</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>How We Share Your Personal Data</b>
        </Typography>
        <br />
        <Typography variant="body2">We may share your data with:</Typography>
        <br />
        <Typography variant="body2">
          <b>Internal</b>
          <ul className={styles.list}>
            <li>BankiFi group companies</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>External</b>
          <ul className={styles.list}>
            <li>Service providers</li>
            <li>Professional advisers</li>
            <li>Regulators and authorities</li>
            <li>Fraud prevention agencies</li>
            <li>Metro Bank Plc</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>International Transfers</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We do not transfer your personal data outside of the UK.
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Data Security</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We implement appropriate security measures to protect your data.
        </Typography>
        <br />
        <Typography variant="body2">We are ISO27001 accredited.</Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Cookies and App Analytics</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We use cookies to support the secure and reliable operation of the Invoice It app and to
          understand how visitors interact with our content.
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Why We Use Cookies</b>
          <ul className={styles.list}>
            <li>Session tracking and troubleshooting</li>
            <li>
              Performance and usage analysis, including capturing app interactions through 3rd–
              party enabled widgets to help us identify and resolve issues
            </li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Cookie Consent</b>
          <br />
          Before enabling session recording and diagnostic features, we request your explicit cookie
          consent by approving the pop up when you login.
          <ul className={styles.list}>
            <li>If you have already provided consent, you will not see the message again</li>
            <li>
              If consent has not yet been provided, a cookie consent message will appear within the
              app requesting your approval
            </li>
            <li>If it is rejected, Invoice It will not ask again for 7 days</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          Once consent is granted:
          <ul className={styles.list}>
            <li>
              App sessions may be recorded and analysed for troubleshooting and service improvement
            </li>
            <li>No session recordings will take place without prior consent</li>
            <li>
              Your consent choice will be saved as a cookie on your device to avoid repeated prompts
            </li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          <b>Managing Your Cookie Preferences</b>
        </Typography>
        <br />
        <Typography variant="body2">
          You can manage or withdraw your cookie consent by clearing cookies from your browser.
          Please note that disabling cookies may limit some troubleshooting and diagnostic features,
          but it will not affect core payment functionality.
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Keeping Your Personal Data</b>
        </Typography>
        <br />
        <Typography variant="body2">
          We retain your data only as long as necessary for:
          <ul className={styles.list}>
            <li>Legal</li>
            <li>Regulatory</li>
            <li>Business purposes</li>
          </ul>
        </Typography>
        <br />

        <Typography className={styles.underline}>
          <b>Your Legal Rights</b>
        </Typography>
        <br />
        <Typography variant="body2">
          You have the right to:
          <ul className={styles.list}>
            <li>Access your data (DSAR)</li>
            <li>Correct your data</li>
            <li>Request erasure</li>
            <li>Object to processing</li>
            <li>Restrict processing</li>
            <li>Transfer your data</li>
            <li>Withdraw consent</li>
          </ul>
        </Typography>
        <br />
        <Typography variant="body2">
          To exercise your rights:
          <br />
          Email:{" "}
          <u>
            <a href="mailto:invoiceit.erasure@bankifi.com">invoiceit.erasure@bankifi.com</a>
          </u>
        </Typography>
        <br />
      </Box>
    </PageLayout>
  );
};

export default PrivacyNoticeScreen;
